// Org chart — Merlin maintains it (kanban b9e6e6af).
//
// Owner ask, 2026-09-28: "I'd like Merlin to also maintain an org chart."
// The roadmap's people directory carries who reports to whom, titles and team
// membership. Merlin places everyone from what it knows (with what each fact
// rests on) and asks each person it is unsure of directly (kanban d556cf3d). This tab shows the trees by reporting line, the
// people not placed yet, and lets an admin set or correct any line.

import { useMemo, useState } from 'react';
import type { OrgChartResponse, OrgNode, RoadmapPersonRequest } from '../../services/roadmapReports';
import { flatten } from './orgChartUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  data: OrgChartResponse;
  /** Admin: save an org edit for one person; omitted → read-only. */
  onEdit?: (name: string, patch: Partial<RoadmapPersonRequest>) => Promise<void>;
}

function Badges({ n }: { n: OrgNode }) {
  return (
    <>
      {n.title && <span className={styles.orgTitle}>{n.title}</span>}
      {n.leads.map((t) => (
        <span key={`l-${t}`} className={styles.orgLead}>lead · {t}</span>
      ))}
      {n.member_of.map((t) => (
        <span key={`m-${t}`} className={styles.orgTeam}>{t}</span>
      ))}
    </>
  );
}

function Basis({ n }: { n: OrgNode }) {
  if (!n.set_by_email) return null;
  const who = n.set_by_email === 'merlin' ? 'Merlin' : n.set_by_email;
  return (
    <span className={styles.orgBasis}>
      set by {who}
      {/* What it rests on (often a quoted thread or an id) opens on request;
          it used to run across the chart as a line of developer text. */}
      {n.set_basis && (
        <details className={styles.orgWhy}>
          <summary>why</summary>
          {n.set_basis}
        </details>
      )}
    </span>
  );
}

export function OrgChartPanel({ data, onEdit }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ reports_to: string; title: string; top: boolean }>({ reports_to: '', title: '', top: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const names = useMemo(() => data.names, [data.names]);
  const all = useMemo(() => [...flatten(data.roots), ...data.unplaced.map((node) => ({ node, depth: 0 }))], [data]);

  const start = (n: OrgNode) => {
    setEditing(n.name);
    setDraft({ reports_to: n.reports_to ?? '', title: n.title ?? '', top: n.top_of_chart });
    setError(null);
  };
  const save = async () => {
    if (!onEdit || !editing) return;
    setBusy(true);
    setError(null);
    try {
      await onEdit(editing, {
        reports_to: draft.top ? null : draft.reports_to.trim() || null,
        title: draft.title.trim() || null,
        top_of_chart: draft.top,
      });
      setEditing(null);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save that — try again.');
    } finally {
      setBusy(false);
    }
  };

  const editor = (n: OrgNode) =>
    editing === n.name ? (
      <div className={styles.orgEditor} data-testid={`org-edit-${n.name}`}>
        <label>
          Reports to
          <select
            aria-label={`Reports to for ${n.name}`}
            value={draft.reports_to}
            disabled={busy || draft.top}
            onChange={(e) => setDraft((d) => ({ ...d, reports_to: e.target.value }))}
          >
            <option value="">— not known —</option>
            {names.filter((x) => x !== n.name).map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Title
          <input aria-label={`Title for ${n.name}`} value={draft.title} disabled={busy} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="as stated" />
        </label>
        <label className={styles.orgCheck}>
          <input type="checkbox" checked={draft.top} disabled={busy} onChange={(e) => setDraft((d) => ({ ...d, top: e.target.checked }))} />
          top of the chart (reports to nobody)
        </label>
        <button type="button" className={styles.respondPrimary} onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={styles.respondGhost} onClick={() => setEditing(null)} disabled={busy}>
          Cancel
        </button>
        {error && <span className={styles.respondError} role="alert">{error}</span>}
      </div>
    ) : null;

  const row = (n: OrgNode) => (
    <div className={styles.orgRow}>
      <span className={styles.orgName}>{n.name}</span>
      <Badges n={n} />
      <Basis n={n} />
      {onEdit && editing !== n.name && (
        <button type="button" className={styles.orgEditBtn} onClick={() => start(n)} title="Set who they report to, their title, or mark them top of the chart">
          Edit
        </button>
      )}
    </div>
  );

  const tree = (nodes: OrgNode[]) => (
    <ul className={styles.orgTree}>
      {nodes.map((n) => (
        <li key={n.name} className={styles.orgNode}>
          {row(n)}
          {editor(n)}
          {n.children.length > 0 && tree(n.children)}
        </li>
      ))}
    </ul>
  );

  return (
    <section className={styles.panel} aria-label="Org chart">
      <div className={styles.panelHead}>
        <h2>Org chart</h2>
        <span className={styles.meta}>
          {all.length} {all.length === 1 ? 'person' : 'people'} · Merlin places everyone from what it knows, and asks the person
          when it is unsure. Each line shows who set it and what it rests on.
        </span>
      </div>
      {data.roots.length === 0 && data.unplaced.length === 0 ? (
        <p className={styles.muted}>Nobody on the chart yet — it fills as Merlin hears names on the roadmap.</p>
      ) : (
        <>
          {data.roots.length > 0 && tree(data.roots)}
          {data.unplaced.length > 0 && (
            <div className={styles.orgUnplaced} data-testid="org-unplaced">
              <h3 className={styles.orgH3}>Not placed yet ({data.unplaced.length})</h3>
              <p className={styles.muted}>
                Merlin asks each of them who they report to on its next run.{onEdit ? ' An admin can set it here now.' : ''}
              </p>
              <ul className={styles.orgTree}>
                {data.unplaced.map((n) => (
                  <li key={n.name} className={styles.orgNode}>
                    {row(n)}
                    {editor(n)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
